import React, { useState, useEffect } from 'react';
import { Radio, RefreshCw, RotateCcw, CheckCircle, XCircle, AlertTriangle, Eye, X, Gauge, Clock, Link2, Activity } from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const LiveBindMonitor = () => {
  const [binds, setBinds] = useState([]);
  const [tpsData, setTpsData] = useState({});
  const [healthData, setHealthData] = useState({});
  const [enquireLinkStatus, setEnquireLinkStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedBind, setSelectedBind] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchData();
    let interval = null;
    if (autoRefresh) {
      interval = setInterval(fetchData, 5000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [autoRefresh]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bindsRes, tpsRes, healthRes, enquireRes] = await Promise.all([
        api.get('/admin/smpp/live-binds'),
        api.get('/admin/smpp/live-binds/tps'),
        api.get('/admin/smpp/live-binds/health'),
        api.get('/admin/smpp/live-binds/enquire-link')
      ]);
      // Map real backend data to expected frontend format
      const rawBinds = bindsRes.data?.data || [];
      const mappedBinds = rawBinds.map(b => {
        let state = 'RED';
        if (b.connection_state === 'CONNECTED') state = 'GREEN';
        else if (b.connection_state === 'CONNECTING') state = 'YELLOW';
        return {
          bind_id: b.connector_id,
          system_id: b.system_id || b.connector_name,
          route_name: b.connector_name,
          bind_type: b.bind_type || 'TRX',
          active_binds: b.num_sockets || 1,
          tps_limit: b.tps_limit,
          connection_state: state,
          last_activity: b.last_enquire_link,
          last_enquire_link: b.last_enquire_link,
          bind_start_time: b.last_bind_time,
          messages_sent: b.messages_sent_today,
          errors_count: b.messages_failed_today,
          is_active: b.is_active,
          connector_id: b.connector_id,
        };
      });
      setBinds(mappedBinds);

      // Map TPS data by system_id
      const rawTps = tpsRes.data?.data || [];
      const tpsMap = {};
      rawTps.forEach(t => { tpsMap[t.connector_name] = t.current_tps; });
      setTpsData(tpsMap);

      // Map health data by system_id
      const rawHealth = healthRes.data?.data || [];
      const healthMap = {};
      rawHealth.forEach(h => { healthMap[h.connector_name] = { health_score: h.health_score, health_status: h.health_status }; });
      setHealthData(healthMap);

      // Map enquire link status by system_id
      const rawEL = enquireRes.data?.data || [];
      const elMap = {};
      rawEL.forEach(e => { elMap[e.connector_name] = e.overdue ? 'TIMEOUT' : (e.last_ping ? 'OK' : 'PENDING'); });
      setEnquireLinkStatus(elMap);

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch bind data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestartBind = async (bindId) => {
    if (window.confirm('Are you sure you want to restart this bind? This will temporarily stop message flow.')) {
      try {
        await api.post(`/admin/smpp/live-binds/${bindId}/restart`);
        fetchData();
      } catch (error) {
        console.error('Failed to restart bind:', error);
      }
    }
  };

  const handleViewDetails = (bind) => {
    setSelectedBind(bind);
    setDetailsDialogOpen(true);
  };

  const getConnectionStyle = (state) => {
    if (state === 'GREEN') return { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' };
    if (state === 'YELLOW') return { background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.2)' };
    if (state === 'RED') return { background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)' };
    return { background: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', border: '1px solid rgba(107, 114, 128, 0.2)' };
  };

  const getEnquireIcon = (status) => {
    if (status === 'OK') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'TIMEOUT') return <XCircle className="w-4 h-4 text-red-500" />;
    if (status === 'PENDING') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    return <Clock className="w-4 h-4 text-gray-400" />;
  };

  const totalTPS = Object.values(tpsData).reduce((sum, tps) => sum + (typeof tps === 'number' ? tps : 0), 0);
  const activeBinds = binds.filter(b => b.connection_state === 'GREEN').length;
  const warningBinds = binds.filter(b => b.connection_state === 'YELLOW').length;
  const criticalBinds = binds.filter(b => b.connection_state === 'RED').length;

  const statCards = [
    { label: 'Active Binds', value: activeBinds, sub: `/${binds.length}`, color: '#059669', bg: 'rgba(16, 185, 129, 0.06)', borderColor: 'rgba(16, 185, 129, 0.12)' },
    { label: 'Warning', value: warningBinds, color: '#d97706', bg: 'rgba(245, 158, 11, 0.06)', borderColor: 'rgba(245, 158, 11, 0.12)' },
    { label: 'Critical', value: criticalBinds, color: '#dc2626', bg: 'rgba(239, 68, 68, 0.06)', borderColor: 'rgba(239, 68, 68, 0.12)' },
    { label: 'Total TPS', value: totalTPS.toFixed(1), color: '#4f46e5', bg: 'rgba(99, 102, 241, 0.06)', borderColor: 'rgba(99, 102, 241, 0.12)' },
  ];

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Radio className="w-5 h-5 text-white" />
            </div>
            Live SMPP Bind Monitor
          </h1>
          <p className="text-sm text-gray-500 mt-1">Real-time SMPP connection monitoring</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer select-none" style={{ background: autoRefresh ? 'rgba(16, 185, 129, 0.08)' : 'rgba(107, 114, 128, 0.06)', border: `1px solid ${autoRefresh ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.12)'}`, color: autoRefresh ? '#059669' : '#6b7280' }}>
            <input type="checkbox" className="sr-only" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            <Activity className="w-3.5 h-3.5" />
            Auto {autoRefresh ? 'On' : 'Off'}
          </label>
          <span className="text-[10px] text-gray-400">{lastUpdated ? lastUpdated.toLocaleTimeString() : ''}</span>
          <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm" style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)', color: '#4f46e5' }}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="rounded-xl p-3 flex items-start gap-3" style={{ background: 'rgba(245, 158, 11, 0.04)', border: '1px solid rgba(245, 158, 11, 0.12)' }}>
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
        <p className="text-xs text-gray-600">Read-only view. Use SMPP Management for start/stop operations. Auto-refresh is disabled by default.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statCards.map((card, i) => (
          <div key={i} className="bg-white rounded-xl p-4" style={{ ...cardStyle, background: card.bg, borderColor: card.borderColor }}>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{card.label}</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</span>
              {card.sub && <span className="text-sm text-gray-400">{card.sub}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="px-5 py-3" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <span className="text-xs font-medium text-gray-500">{binds.length} bind{binds.length !== 1 ? 's' : ''}</span>
        </div>
        {loading && binds.length === 0 ? (
          <div className="p-16 flex flex-col items-center">
            <RefreshCw className="w-8 h-8 animate-spin mb-3" style={{ color: '#6366f1' }} />
            <p className="text-sm text-gray-500">Loading binds...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">System ID</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Route</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Binds</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">TPS</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Limit</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">EL</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Last Activity</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">State</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {binds.length === 0 ? (
                  <tr><td colSpan="10" className="px-6 py-12 text-center"><Radio className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} /><p className="text-sm text-gray-400 font-medium">No active binds</p></td></tr>
                ) : binds.map((bind) => (
                  <tr key={bind.bind_id} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                    <td className="px-4 py-3 text-xs font-mono font-semibold text-gray-800">{bind.system_id}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{bind.route_name}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' }}>{bind.bind_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Link2 className="w-3 h-3 text-gray-400" />
                        <span className="text-xs font-semibold text-gray-700">{bind.active_binds}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Gauge className="w-3 h-3 text-gray-400" />
                        <span className="text-xs font-semibold text-gray-700">{tpsData[bind.system_id] || 0}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{bind.tps_limit || 'N/A'}</td>
                    <td className="px-4 py-3" title={enquireLinkStatus[bind.system_id] || 'UNKNOWN'}>
                      {getEnquireIcon(enquireLinkStatus[bind.system_id])}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className="text-[10px] text-gray-500">{bind.last_activity ? new Date(bind.last_activity).toLocaleTimeString() : 'Never'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold" style={getConnectionStyle(bind.connection_state)}>{bind.connection_state}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => handleViewDetails(bind)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50" title="View Details"><Eye className="w-3.5 h-3.5" style={{ color: '#6366f1' }} /></button>
                        {bind.connection_state !== 'RESTARTING' && (
                          <button onClick={() => handleRestartBind(bind.bind_id)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-amber-50" title="Restart Bind"><RotateCcw className="w-3.5 h-3.5 text-amber-500" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bind Details Modal */}
      {detailsDialogOpen && selectedBind && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 overflow-y-auto" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl mb-8" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}><Radio className="w-3.5 h-3.5 text-white" /></div>
                Bind Details
              </h3>
              <button onClick={() => setDetailsDialogOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Connection</h4>
                  <div className="space-y-2">
                    {[
                      ['System ID', selectedBind.system_id],
                      ['Route', selectedBind.route_name],
                      ['Bind Type', selectedBind.bind_type],
                      ['Active Binds', selectedBind.active_binds],
                      ['State', selectedBind.connection_state],
                    ].map(([k, v], i) => (
                      <div key={i} className="flex justify-between py-1" style={i > 0 ? { borderTop: '1px solid rgba(99, 102, 241, 0.05)' } : {}}>
                        <span className="text-[10px] text-gray-400">{k}</span>
                        <span className="text-xs font-mono text-gray-800">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Performance</h4>
                  <div className="space-y-2">
                    {[
                      ['Current TPS', tpsData[selectedBind.system_id] || 0],
                      ['TPS Limit', selectedBind.tps_limit || 'N/A'],
                      ['Health', `${healthData[selectedBind.system_id]?.health_score || 0}%`],
                      ['Msgs Sent', selectedBind.messages_sent || 0],
                      ['Errors', selectedBind.errors_count || 0],
                    ].map(([k, v], i) => (
                      <div key={i} className="flex justify-between py-1" style={i > 0 ? { borderTop: '1px solid rgba(99, 102, 241, 0.05)' } : {}}>
                        <span className="text-[10px] text-gray-400">{k}</span>
                        <span className="text-xs font-mono text-gray-800">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Timing</h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {[
                    ['Bind Start', selectedBind.bind_start_time ? new Date(selectedBind.bind_start_time).toLocaleString() : 'Unknown'],
                    ['Last Activity', selectedBind.last_activity ? new Date(selectedBind.last_activity).toLocaleString() : 'Never'],
                    ['Last EL', selectedBind.last_enquire_link ? new Date(selectedBind.last_enquire_link).toLocaleString() : 'Never'],
                    ['Avg Latency', `${selectedBind.avg_latency || 0}ms`],
                  ].map(([k, v], i) => (
                    <div key={i} className="flex justify-between py-1" style={i > 1 ? { borderTop: '1px solid rgba(99, 102, 241, 0.05)' } : {}}>
                      <span className="text-[10px] text-gray-400">{k}</span>
                      <span className="text-[10px] font-mono text-gray-700">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveBindMonitor;
